import React, { useRef, useEffect, useCallback } from "react";
import { Compass, Users, MapPin } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { getRelationDetails } from "./utils";

const AdjacentPropertiesMap = ({
    analysisMode,
    isLoadingNeighbours,
    neighbourProperties,
    lookupBy,
    selectedPlaceLocation,
    selectedParcelGeometry,
    mergedLots,
    hoveredNeighbour,
    setHoveredNeighbour,
    onNeighbourClick,
    onToggleMerge,
    primaryAddress
}) => {
    // Ref for the registry scroll container
    const registryScrollRef = useRef(null);
    // Ref for the Google Map container div
    const mapContainerRef = useRef(null);
    // Ref for the Google Map instance
    const mapInstanceRef = useRef(null);
    // Ref for markers
    const markersRef = useRef([]);
    // Ref for the center marker
    const centerMarkerRef = useRef(null);
    // Ref for parcel boundary polygons
    const polygonsRef = useRef([]);

    // Auto-scroll the registry list to the hovered property
    useEffect(() => {
        if (!hoveredNeighbour || !registryScrollRef.current) return;
        const target = registryScrollRef.current.querySelector(`[data-address="${CSS.escape(hoveredNeighbour)}"]`);
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [hoveredNeighbour]);

    // Center coordinates
    const centerLat = selectedPlaceLocation ? (typeof selectedPlaceLocation.lat === 'function' ? selectedPlaceLocation.lat() : selectedPlaceLocation.lat) : 0;
    const centerLng = selectedPlaceLocation ? (typeof selectedPlaceLocation.lng === 'function' ? selectedPlaceLocation.lng() : selectedPlaceLocation.lng) : 0;

    // Convert ArcGIS rings (array of [lng, lat] pairs) to Google Maps LatLng path
    const ringsToGooglePath = useCallback((rings) => {
        if (!rings || !window.google?.maps) return [];
        return rings.map(ring =>
            ring.map(pt => ({ lat: pt[1], lng: pt[0] }))
        );
    }, []);

    // Create SVG marker icon as data URL
    const createMarkerIcon = useCallback((label, isHovered, isMerged, isCenter = false) => {
        if (!window.google?.maps) return null;

        if (isCenter) {
            return {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="52" viewBox="0 0 44 52">
                        <defs>
                            <filter id="shadow" x="-30%" y="-10%" width="160%" height="140%">
                                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(16,185,129,0.3)"/>
                            </filter>
                        </defs>
                        <path d="M22 50 C22 50 42 32 42 20 C42 9 33 2 22 2 C11 2 2 9 2 20 C2 32 22 50 22 50Z" 
                               fill="url(#grad)" stroke="white" stroke-width="2.5" filter="url(#shadow)"/>
                        <defs>
                            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style="stop-color:#10b981"/>
                                <stop offset="100%" style="stop-color:#059669"/>
                            </linearGradient>
                        </defs>
                        <circle cx="22" cy="19" r="9" fill="white" opacity="0.25"/>
                        <text x="22" y="23" text-anchor="middle" fill="white" font-size="12" font-weight="900" font-family="system-ui, -apple-system, sans-serif">⌂</text>
                    </svg>
                `)}`,
                scaledSize: new window.google.maps.Size(44, 52),
                anchor: new window.google.maps.Point(22, 50),
            };
        }

        const bgColor = isHovered ? '#f59e0b' : isMerged ? '#4f46e5' : '#ffffff';
        const borderColor = isHovered ? '#d97706' : isMerged ? '#3730a3' : '#cbd5e1';
        const textColor = isHovered ? '#ffffff' : isMerged ? '#ffffff' : '#475569';
        const borderWidth = isHovered ? '2.5' : '2';
        const size = isHovered ? 42 : 36;
        const fontSize = isHovered ? '12' : '10';
        const dotEl = isMerged ? `<circle cx="${size - 6}" cy="6" r="4.5" fill="#10b981" stroke="white" stroke-width="1"/>` : '';

        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
                    <defs>
                        <filter id="ds" x="-20%" y="-10%" width="140%" height="140%">
                            <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="rgba(0,0,0,${isHovered ? '0.25' : '0.12'})"/>
                        </filter>
                    </defs>
                    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${bgColor}" stroke="${borderColor}" stroke-width="${borderWidth}" filter="url(#ds)"/>
                    ${dotEl}
                    <text x="${size / 2}" y="${size / 2 + 4}" text-anchor="middle" fill="${textColor}" font-size="${fontSize}" font-weight="800" font-family="system-ui, -apple-system, sans-serif">${label}</text>
                </svg>
            `)}`,
            scaledSize: new window.google.maps.Size(size, size),
            anchor: new window.google.maps.Point(size / 2, size / 2),
        };
    }, []);

    // Initialize the Google Map + markers + parcel boundaries
    useEffect(() => {
        if (!mapContainerRef.current || !window.google?.maps || !centerLat || !centerLng) return;
        if (neighbourProperties.length === 0) return;

        // Create map only once
        if (!mapInstanceRef.current) {
            mapInstanceRef.current = new window.google.maps.Map(mapContainerRef.current, {
                center: { lat: centerLat, lng: centerLng },
                zoom: 18,
                mapTypeId: 'satellite',
                disableDefaultUI: true,
                zoomControl: true,
                zoomControlOptions: {
                    position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
                },
                gestureHandling: 'cooperative',
                styles: [],
                tilt: 0,
            });
        } else {
            mapInstanceRef.current.setCenter({ lat: centerLat, lng: centerLng });
        }

        const map = mapInstanceRef.current;

        // Clear existing markers
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];
        if (centerMarkerRef.current) {
            centerMarkerRef.current.setMap(null);
            centerMarkerRef.current = null;
        }

        // Clear existing polygons
        polygonsRef.current.forEach(p => p.setMap(null));
        polygonsRef.current = [];

        // ── Draw subject property parcel boundary ──
        if (selectedParcelGeometry && selectedParcelGeometry.length > 0) {
            const paths = ringsToGooglePath(selectedParcelGeometry);
            paths.forEach(path => {
                const polygon = new window.google.maps.Polygon({
                    paths: path,
                    map,
                    strokeColor: '#10b981',
                    strokeWeight: 3,
                    strokeOpacity: 0.95,
                    fillColor: '#10b981',
                    fillOpacity: 0.15,
                    zIndex: 5,
                });
                polygonsRef.current.push(polygon);
            });
        }

        // ── Draw neighbour parcel boundaries ──
        neighbourProperties.forEach((neighbour) => {
            if (!neighbour.parcelRings || neighbour.parcelRings.length === 0) return;

            const isMerged = mergedLots.some(l => (neighbour.apn && l.apn) ? l.apn === neighbour.apn : l.address === neighbour.address);
            const paths = ringsToGooglePath(neighbour.parcelRings);

            paths.forEach(path => {
                const polygon = new window.google.maps.Polygon({
                    paths: path,
                    map,
                    strokeColor: isMerged ? '#4f46e5' : '#e2e8f0',
                    strokeWeight: isMerged ? 3 : 1.5,
                    strokeOpacity: isMerged ? 0.95 : 0.8,
                    fillColor: isMerged ? '#4f46e5' : '#ffffff',
                    fillOpacity: isMerged ? 0.2 : 0.05,
                    zIndex: isMerged ? 4 : 2,
                });

                // Polygon hover: highlight the boundary
                polygon.addListener('mouseover', () => {
                    setHoveredNeighbour(neighbour.address);
                });
                polygon.addListener('mouseout', () => {
                    setHoveredNeighbour(null);
                });
                polygon.addListener('click', () => {
                    onNeighbourClick(neighbour);
                });

                // Store reference to neighbour for hover updates
                polygon._neighbourAddress = neighbour.address;
                polygon._neighbourApn = neighbour.apn || null;
                polygonsRef.current.push(polygon);
            });
        });

        // ── Add center (subject property) marker ──
        const centerIcon = createMarkerIcon('', false, false, true);
        if (centerIcon) {
            centerMarkerRef.current = new window.google.maps.Marker({
                position: { lat: centerLat, lng: centerLng },
                map,
                icon: centerIcon,
                zIndex: 100,
                title: primaryAddress || 'Subject Property',
            });
        }

        // ── Add neighbour markers ──
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({ lat: centerLat, lng: centerLng });

        neighbourProperties.forEach((neighbour) => {
            if (!neighbour.lat || !neighbour.lng) return;

            const isMerged = mergedLots.some(l => (neighbour.apn && l.apn) ? l.apn === neighbour.apn : l.address === neighbour.address);
            const streetNumber = lookupBy === "address"
                ? ((neighbour.address || '').trim().split(' ')[0] || 'Lot')
                : (neighbour.apn ? neighbour.apn.slice(-4) : ((neighbour.address || '').trim().split(' ')[0] || 'Lot'));
            const icon = createMarkerIcon(streetNumber, false, isMerged);

            if (!icon) return;

            const marker = new window.google.maps.Marker({
                position: { lat: neighbour.lat, lng: neighbour.lng },
                map,
                icon,
                zIndex: isMerged ? 50 : 10,
                title: neighbour.name || neighbour.address,
            });

            marker._neighbourData = neighbour;
            marker._streetNumber = streetNumber;

            marker.addListener('mouseover', () => {
                setHoveredNeighbour(neighbour.address);
            });
            marker.addListener('mouseout', () => {
                setHoveredNeighbour(null);
            });
            marker.addListener('click', () => {
                onNeighbourClick(neighbour);
            });

            bounds.extend({ lat: neighbour.lat, lng: neighbour.lng });
            markersRef.current.push(marker);
        });

        // Fit map to show all markers with padding
        map.fitBounds(bounds, { top: 30, bottom: 30, left: 30, right: 30 });

        // Don't zoom in too much
        const listener = window.google.maps.event.addListener(map, 'idle', () => {
            if (map.getZoom() > 19) map.setZoom(19);
            window.google.maps.event.removeListener(listener);
        });

    }, [neighbourProperties, centerLat, centerLng, mergedLots, selectedParcelGeometry, createMarkerIcon, ringsToGooglePath, primaryAddress, setHoveredNeighbour, onNeighbourClick, lookupBy]);

    // Update marker icons + polygon styles when hover/merge state changes
    useEffect(() => {
        if (!window.google?.maps) return;

        // Update marker icons
        markersRef.current.forEach(marker => {
            const neighbour = marker._neighbourData;
            if (!neighbour) return;

            const isMerged = mergedLots.some(l => (neighbour.apn && l.apn) ? l.apn === neighbour.apn : l.address === neighbour.address);
            const isHovered = hoveredNeighbour === neighbour.address;
            const streetNumber = marker._streetNumber;
            const icon = createMarkerIcon(streetNumber, isHovered, isMerged);

            if (icon) {
                marker.setIcon(icon);
                marker.setZIndex(isHovered ? 200 : isMerged ? 50 : 10);
            }
        });

        // Update polygon boundary styles on hover
        polygonsRef.current.forEach(polygon => {
            const addr = polygon._neighbourAddress;
            const apn = polygon._neighbourApn;
            if (!addr) return; // skip subject property polygon

            const isMerged = mergedLots.some(l => apn ? (l.apn === apn) : (l.address === addr));
            const isHovered = hoveredNeighbour === addr;

            if (isHovered) {
                polygon.setOptions({
                    strokeColor: '#f59e0b',
                    strokeWeight: 3,
                    strokeOpacity: 1,
                    fillColor: '#f59e0b',
                    fillOpacity: 0.25,
                    zIndex: 8,
                });
            } else if (isMerged) {
                polygon.setOptions({
                    strokeColor: '#4f46e5',
                    strokeWeight: 3,
                    strokeOpacity: 0.95,
                    fillColor: '#4f46e5',
                    fillOpacity: 0.2,
                    zIndex: 4,
                });
            } else {
                polygon.setOptions({
                    strokeColor: '#e2e8f0',
                    strokeWeight: 1.5,
                    strokeOpacity: 0.8,
                    fillColor: '#ffffff',
                    fillOpacity: 0.05,
                    zIndex: 2,
                });
            }
        });
    }, [hoveredNeighbour, mergedLots, createMarkerIcon]);

    // Cleanup map on unmount
    useEffect(() => {
        return () => {
            markersRef.current.forEach(m => m.setMap(null));
            markersRef.current = [];
            polygonsRef.current.forEach(p => p.setMap(null));
            polygonsRef.current = [];
            if (centerMarkerRef.current) {
                centerMarkerRef.current.setMap(null);
                centerMarkerRef.current = null;
            }
            mapInstanceRef.current = null;
        };
    }, []);

    if (analysisMode !== "merged") return null;
    if (!isLoadingNeighbours && neighbourProperties.length === 0 && !selectedPlaceLocation) return null;

    // Generate uniqueIds for the registry list
    const neighboursWithIds = neighbourProperties.map((n, idx) => ({
        ...n,
        uniqueId: (n.placeId || n.address) + '-' + (n.relation || idx)
    }));

    return (
        <AnimatePresence>
            {(isLoadingNeighbours || neighbourProperties.length > 0) && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                >
                    <div className="mt-4 rounded-2xl border border-slate-200/80 shadow-[0_12px_40px_rgb(0,0,0,0.04)] relative overflow-hidden bg-white">

                        {/* Decorative top accent bar */}
                        <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />

                        {/* Header */}
                        <div className="px-5 py-3.5 flex items-center justify-between border-b border-slate-100 bg-gradient-to-b from-slate-50/60 to-white">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-500/10 shrink-0">
                                    <MapPin className="w-4 h-4 text-white" />
                                </div>
                                <div className="min-w-0">
                                    <h4 className="text-sm font-bold text-slate-800 tracking-tight">Adjacent Properties Map</h4>
                                    <p className="text-[11px] text-slate-400 font-medium truncate">
                                        {primaryAddress
                                            ? <><span className="text-indigo-500 font-semibold">{primaryAddress.split(',')[0]}</span> · Satellite + Boundaries</>
                                            : 'Interactive satellite map · Click parcels to select'
                                        }
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 rounded-lg border border-blue-100 shrink-0">
                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                                <span className="text-[10px] font-extrabold text-blue-600 uppercase tracking-wider">{neighbourProperties.length} Found</span>
                            </div>
                        </div>

                        {/* Loading State */}
                        {isLoadingNeighbours && (
                            <div className="py-16 flex flex-col items-center justify-center gap-4">
                                <div className="relative">
                                    <div className="w-12 h-12 rounded-full border-2 border-slate-100" />
                                    <div className="absolute inset-0 w-12 h-12 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                                    <div className="absolute inset-2 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                                        <Compass className="w-4 h-4 text-blue-500 animate-pulse" />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <p className="text-xs font-semibold text-slate-700">Resolving GIS Surrounds...</p>
                                </div>
                            </div>
                        )}

                        {/* Body Split Layout */}
                        {!isLoadingNeighbours && neighbourProperties.length > 0 && (
                            <div className="p-5 flex flex-col md:flex-row gap-6 items-stretch">

                                {/* Left Column: Real Google Map with Parcel Boundaries */}
                                <div className="relative w-full md:w-[460px] lg:w-[520px] shrink-0">
                                    <div
                                        ref={mapContainerRef}
                                        className="w-full h-[350px] rounded-2xl overflow-hidden border border-slate-200/80 shadow-sm"
                                        style={{ minHeight: '350px' }}
                                    />
                                    {/* Map legend overlay */}
                                    <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-xl p-2.5 shadow-[0_8px_30px_rgba(0,0,0,0.08)] border border-slate-200/50 z-10 flex flex-col gap-2 min-w-[125px] transition-all duration-300">
                                        
                                        
                                        <div className="flex flex-row gap-1.5">
                                            {/* Subject */}
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-md bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center shadow-sm shadow-emerald-500/10 shrink-0">
                                                    <span className="text-[9px] text-emerald-600 font-extrabold">⌂</span>
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-[9px] text-slate-700 font-extrabold leading-tight">Subject</span>
                                                    <span className="text-[7.5px] text-slate-400 font-medium leading-none truncate">Target parcel</span>
                                                </div>
                                            </div>

                                            {/* Merged */}
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-md bg-indigo-600 border border-indigo-700 flex items-center justify-center shadow-sm shadow-indigo-600/10 shrink-0 relative">
                                                    <span className="text-[7px] text-white font-extrabold">✓</span>
                                                    <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400 border border-white" />
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-[9px] text-slate-700 font-extrabold leading-tight">Merged</span>
                                                    <span className="text-[7.5px] text-slate-400 font-medium leading-none truncate">Combined lot</span>
                                                </div>
                                            </div>

                                            {/* Hovered */}
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-md bg-amber-500 border border-amber-600 flex items-center justify-center shadow-sm shrink-0">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-[9px] text-slate-700 font-extrabold leading-tight">Hovered</span>
                                                    <span className="text-[7.5px] text-slate-400 font-medium leading-none truncate">Focused lot</span>
                                                </div>
                                            </div>

                                            {/* Neighbour */}
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-md bg-white border border-slate-300 flex items-center justify-center shadow-sm shrink-0">
                                                    <span className="text-[8px] text-slate-500 font-bold">123</span>
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-[9px] text-slate-700 font-extrabold leading-tight">Neighbour</span>
                                                    <span className="text-[7.5px] text-slate-400 font-medium leading-none truncate">Adjacent lot</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Divider on desktop */}
                                <div className="hidden md:block w-px bg-slate-100 shrink-0 self-stretch" />

                                {/* Right Column: Surrounding Lots Scrollable List */}
                                <div className="flex-1 flex flex-col min-w-0 justify-between">
                                    <div className="flex items-center justify-between mb-3">
                                        <h5 className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                                            <Users className="w-3.5 h-3.5 text-slate-500" />
                                            Surrounding Registry ({neighbourProperties.length})
                                        </h5>
                                        <span className="text-[9.5px] text-slate-400 font-medium">Click name to target lot</span>
                                    </div>
                                    <div ref={registryScrollRef} className="space-y-2 overflow-y-auto max-h-[295px] pr-1.5 scrollbar-thin scrollbar-thumb-slate-200">
                                        {neighboursWithIds.sort((a, b) => (a.distance || 0) - (b.distance || 0)).map((neighbour) => {
                                            const details = getRelationDetails(neighbour.relation);
                                            const isMerged = mergedLots.some(l => (neighbour.apn && l.apn) ? l.apn === neighbour.apn : l.address === neighbour.address);
                                            const isHovered = hoveredNeighbour === neighbour.address;
                                            return (
                                                <div
                                                    key={'registry-' + neighbour.uniqueId}
                                                    data-address={neighbour.address}
                                                    onMouseEnter={() => setHoveredNeighbour(neighbour.address)}
                                                    onMouseLeave={() => setHoveredNeighbour(null)}
                                                    className={'relative flex items-center justify-between gap-3 p-3 rounded-xl border transition-all duration-200 group cursor-pointer '
                                                        + (isHovered
                                                            ? 'bg-slate-800 border-slate-700 shadow-lg shadow-slate-900/15 -translate-y-0.5 z-10'
                                                            : isMerged
                                                                ? 'bg-blue-50/50 border-blue-200 shadow-sm ring-1 ring-blue-100'
                                                                : 'bg-white border-slate-100 hover:border-slate-200 hover:shadow-sm hover:-translate-y-0.5'
                                                        )
                                                    }
                                                    onClick={() => onNeighbourClick(neighbour)}
                                                >
                                                    <div className="flex-1 min-w-0 flex items-center gap-2.5">
                                                        <span className={`text-[8.5px] font-extrabold uppercase px-2 py-0.5 rounded border shrink-0 tracking-wider transition-colors duration-150 ${isHovered ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' : details.badgeColor}`}>
                                                            {details.label.replace(/^[^\w]*/, '').trim()}
                                                        </span>
                                                        <div className="min-w-0">
                                                            <h6 className={'text-xs font-bold truncate transition-colors leading-tight ' + (isHovered ? 'text-white' : 'text-slate-700 group-hover:text-blue-600')}>
                                                                {lookupBy === "address"
                                                                    ? (neighbour.apn 
                                                                        ? `${neighbour.name || neighbour.address.split(',')[0]} (${neighbour.apn})`
                                                                        : (neighbour.name || neighbour.address.split(',')[0]))
                                                                    : (neighbour.apn
                                                                        ? `${neighbour.apn} (${neighbour.name || neighbour.address.split(',')[0]})`
                                                                        : (neighbour.name || neighbour.address.split(',')[0]))
                                                                }
                                                            </h6>
                                                            <p className={'text-[10px] truncate mt-0.5 font-medium leading-none transition-colors ' + (isHovered ? 'text-slate-400' : 'text-slate-400')}>
                                                                {neighbour.address}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {neighbour.distance && (
                                                            <span className={'text-[10px] font-semibold px-1.5 py-0.5 rounded-md whitespace-nowrap transition-colors ' + (isHovered ? 'text-slate-300 bg-white/10' : 'text-slate-400 bg-slate-50 border border-slate-100')}>
                                                                {neighbour.distance}m
                                                            </span>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); onToggleMerge(neighbour, e); }}
                                                            className={'text-[10px] font-extrabold px-3 py-1 rounded-xl border transition-all duration-200 cursor-pointer ' + (isMerged ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-0 shadow-sm' : isHovered ? 'bg-white text-slate-800 border-white/80 hover:bg-slate-50' : 'bg-slate-50 text-slate-500 border-slate-200 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50')}
                                                        >
                                                            {isMerged ? "✓ Merged" : "+ Merge"}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* Empty State */}
                        {!isLoadingNeighbours && neighbourProperties.length === 0 && selectedPlaceLocation && (
                            <div className="px-5 py-12 text-center bg-slate-50 border-t border-slate-100">
                                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                                    <Compass className="w-5 h-5 text-slate-400" />
                                </div>
                                <p className="text-sm font-semibold text-slate-500">No neighbours discovered</p>
                                <p className="text-xs text-slate-400 mt-1">This location might not have adjacent parcels in the GIS database</p>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AdjacentPropertiesMap;
